import React, { memo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { DialogTitleWithClose } from '../../common';
import PropTypes from 'prop-types';
import {
  ExecutionHistoryControls,
  ExecutionHistoryList,
} from './ExecutionHistoryDialogComponents';

const ExecutionHistoryDialog = ({
  open,
  template,
  executions,
  loading,
  onClose,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  pagination = {},
}) => {
  // Pagination state from props (server-side)
  const {
    page = 1,
    page_size = 10,
    total_count = 0,
    total_pages = 1,
  } = pagination;

  // Sort state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Handle pagination changes (server-side)
  const handleChangePage = (event, newPage) => {
    if (onPageChange) {
      onPageChange(newPage + 1); // API uses 1-based page numbers
    }
  };

  const handleChangeRowsPerPage = event => {
    const newPageSize = parseInt(event.target.value, 10);
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  // Handle sorting (server-side)
  const handleSortChange = event => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    if (onSortChange) {
      onSortChange(newSortBy, sortOrder);
    }
  };

  const toggleSortOrder = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
    if (onSortChange) {
      onSortChange(sortBy, newSortOrder);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitleWithClose
        title={
          <Box>
            <Typography variant="h6">
              Execution History - {template?.name}
            </Typography>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                Template ID: {template?.id}
              </Typography>
            </Box>
          </Box>
        }
        onClose={onClose}
        disableClose={loading}
      />
      <DialogContent>
        <ExecutionHistoryControls
          page={page}
          pageSize={page_size}
          totalCount={total_count}
          totalPages={total_pages}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageSizeChange={handleChangeRowsPerPage}
          onSortChange={handleSortChange}
          onSortOrderToggle={toggleSortOrder}
          onPageChange={handleChangePage}
        />
        <ExecutionHistoryList
          executions={executions}
          loading={loading}
          totalCount={total_count}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

ExecutionHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  template: PropTypes.object,
  executions: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onSortChange: PropTypes.func,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    page_size: PropTypes.number,
    total_count: PropTypes.number,
    total_pages: PropTypes.number,
  }),
};

export default memo(ExecutionHistoryDialog);
